import {
  broadcastFailedJobsDocument,
  broadcastQueueStatusDocument,
} from '@/features/broadcast/graphql/queue-status';
import { useQuery } from '@apollo/client';
import {
  Badge,
  Button,
  Group,
  Paper,
  Progress,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

export interface BroadcastRun {
  total: number; // recipients enqueued (sendBroadcast return)
  sentAt: number; // ms epoch when the send was triggered
  baselineCompleted: number; // queue `completed` just before sending
  baselineFailed: number; // queue `failed` just before sending
}

const POLL_INTERVAL = 1500;

const formatDuration = (totalSeconds: number) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return `${m}:${String(rest).padStart(2, '0')}`;
};

interface StatTileProps {
  label: string;
  value: string | number;
  color?: string;
}

const StatTile = ({ label, value, color }: StatTileProps) => (
  <Paper withBorder p="sm" radius="md">
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text fw={700} size="xl" c={color}>
      {value}
    </Text>
  </Paper>
);

interface BroadcastProgressProps {
  run: BroadcastRun;
  onClose: () => void;
}

export const BroadcastProgress = ({ run, onClose }: BroadcastProgressProps) => {
  // Drives polling via the option (not stopPolling()) so it reliably halts once
  // done — a static pollInterval would otherwise restart polling on re-render.
  const [polling, setPolling] = useState(true);

  const { data: statusData, refetch: refetchStatus } = useQuery(broadcastQueueStatusDocument, {
    fetchPolicy: 'network-only',
    pollInterval: polling ? POLL_INTERVAL : 0,
  });

  const { data: failedData, refetch: refetchFailed } = useQuery(broadcastFailedJobsDocument, {
    variables: { limit: 500, skip: 0 },
    fetchPolicy: 'network-only',
    pollInterval: polling ? POLL_INTERVAL : 0,
  });

  const status = statusData?.broadcastQueueStatus;

  const succeeded = status ? Math.max(0, status.completed - run.baselineCompleted) : 0;
  const failed = status ? Math.max(0, status.failed - run.baselineFailed) : 0;
  const processed = Math.min(run.total, succeeded + failed);
  const pending = Math.max(0, run.total - processed);
  const done = run.total === 0 || pending === 0;

  const progressPct = run.total > 0 ? (processed / run.total) * 100 : 100;
  const succeededPct = run.total > 0 ? (succeeded / run.total) * 100 : 0;
  const failedPct = run.total > 0 ? (failed / run.total) * 100 : 0;

  // Ticking clock so elapsed/rate update between polls; stops once done.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (done) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [done]);

  // Stop polling once everything is processed (keep the final numbers on screen).
  useEffect(() => {
    if (done) setPolling(false);
  }, [done]);

  const elapsedSeconds = Math.max(0, (now - run.sentAt) / 1000);
  const ratePerSec = elapsedSeconds > 0 ? processed / elapsedSeconds : 0;
  const ratePerMin = ratePerSec * 60;
  const etaSeconds = ratePerSec > 0 && pending > 0 ? pending / ratePerSec : null;

  // Only failures from this send (within the retention window).
  const currentFailures = (failedData?.broadcastFailedJobs ?? [])
    .filter((job) => job.failedAt != null && job.failedAt >= run.sentAt)
    .sort((a, b) => (b.failedAt ?? 0) - (a.failedAt ?? 0));

  const statusBadge = done ? (
    failed > 0 ? (
      <Badge color="orange">Dokončené s chybami</Badge>
    ) : (
      <Badge color="teal">Dokončené</Badge>
    )
  ) : (
    <Badge color="blue">Odosiela sa…</Badge>
  );

  const handleRefresh = () => {
    void refetchStatus();
    void refetchFailed();
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="sm">
            <Text fw={600}>Priebeh broadcastu</Text>
            {statusBadge}
          </Group>
          <Group gap="xs">
            <Tooltip label="Obnoviť">
              <Button variant="light" size="xs" onClick={handleRefresh}>
                Obnoviť
              </Button>
            </Tooltip>
            <Button variant="subtle" color="gray" size="xs" onClick={onClose}>
              Zavrieť
            </Button>
          </Group>
        </Group>

        <Stack gap={4}>
          <Progress.Root size="xl" radius="sm">
            <Progress.Section value={succeededPct} color="teal" />
            <Progress.Section value={failedPct} color="red" />
          </Progress.Root>
          <Text size="sm" c="dimmed">
            {processed} / {run.total} spracovaných ({Math.round(progressPct)} %)
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <StatTile label="Odoslané" value={succeeded} color="teal" />
          <StatTile label="Zlyhané" value={failed} color={failed > 0 ? 'red' : undefined} />
          <StatTile label="Čakajúce" value={pending} />
          <StatTile label="Spolu" value={run.total} />
        </SimpleGrid>

        <Group justify="space-between" wrap="wrap">
          <Text size="sm">
            <Text span c="dimmed">
              Rýchlosť:{' '}
            </Text>
            {ratePerSec > 0 ? `${ratePerSec.toFixed(1)}/s · ${Math.round(ratePerMin)}/min` : '—'}
          </Text>
          <Text size="sm">
            <Text span c="dimmed">
              Uplynulo:{' '}
            </Text>
            {formatDuration(elapsedSeconds)}
          </Text>
          <Text size="sm">
            <Text span c="dimmed">
              Odhad dokončenia:{' '}
            </Text>
            {done ? 'hotovo' : etaSeconds != null ? `~${formatDuration(etaSeconds)}` : '—'}
          </Text>
          {status && (
            <Text size="sm" c="dimmed">
              v poradí {status.waiting} · práve odosiela {status.active}
            </Text>
          )}
        </Group>

        {currentFailures.length > 0 && (
          <Stack gap="xs">
            <Text fw={500} c="red">
              Zlyhaní príjemcovia ({currentFailures.length})
            </Text>
            <ScrollArea.Autosize mah={260}>
              <Table striped highlightOnHover withTableBorder stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Meno</Table.Th>
                    <Table.Th>Dôvod</Table.Th>
                    <Table.Th>Čas</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {currentFailures.map((job) => (
                    <Table.Tr key={job.id}>
                      <Table.Td>{job.name || job.pageId}</Table.Td>
                      <Table.Td>
                        <Text size="sm" style={{ overflowWrap: 'anywhere' }}>
                          {job.reason ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {job.failedAt ? dayjs(job.failedAt).format('HH:mm:ss') : '—'}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
